import { createElement } from 'react'
import {
  LogIn,
  Target,
  Shield,
  PieChart,
  TrendingUp,
  ArrowRight,
  GitBranch,
  TrendingDown,
} from 'lucide-react'
import type { UniversalNodeSchema } from '../universal-node-schema'
import { UniversalNodeRegistry } from '../universal-node-schema'
import { entryWindowInput, entryWindowParameters } from './entry-window-schema'
import { TradeRunSection } from '../../components/nodes/trade-run-section'

// Run lives on the node that places the trades (Run mode was removed
// 2026-10-05). Simple Trade and Advanced Trade keep it until strategies built
// with them move to the Trade Manager.
const runSection = {
  customSections: [
    {
      type: 'custom' as const,
      position: 'after-parameters' as const,
      render: () => createElement(TradeRunSection),
    },
  ],
}

// Entry Rules Node
const entryRulesSchema: UniversalNodeSchema = {
  type: 'entryRules',
  // The Trade Manager replaces it; still loads, no longer added (2026-10-06).
  addable: false,
  // Part of the cluster the Trade Manager lowers to; the AI sets its entry on
  // the Trade Manager (2026-10-06 vocabulary swap).
  compound: { placesTrades: true, catalog: { ai: false } },
  facets: { entryWindow: { trigger: 'signal' } },
  // A mode that stops reading the signal lets go of its wire
  // (utils/graph-commands planParamChange).
  face: { releaseInactive: true },
  displayName: 'Entry Rules',
  category: 'entry-exit-logic',
  icon: LogIn,
  inputHandler: {
    type: 'single',
  },
  inputs: [
    {
      id: 'signal',
      displayName: '',
      acceptedTypes: ['number', 'ohlcv'],
      acceptedSources: ['*'],
      // Market entries fill at the bar close — the signal price reference is
      // only load-bearing for Limit orders (their offset applies to it). The
      // cookbook's "wired to close so the graph is strategy-complete" wiring
      // was validator appeasement, not an engine need (Chris, 2026-07-17).
      activeWhen: { parameter: 'entryType', is: ['Limit'] },
      fieldSelection: {
        options: [
          { value: 'close', label: 'Close' },
          { value: 'high', label: 'High' },
          { value: 'low', label: 'Low' },
          { value: 'open', label: 'Open' },
        ],
        defaultField: 'close',
      },
    },
    entryWindowInput,
  ],
  outputs: [
    {
      id: 'entry',
      displayName: 'Entry',
      type: 'boolean',
    },
  ],
  parameters: [
    {
      name: 'entryType',
      displayName: 'Entry Type',
      type: 'select',
      default: 'Market',
      options: [
        { value: 'Market', label: 'Market' },
        { value: 'Limit', label: 'Limit' },
      ],
    },
    {
      name: 'orderDirection',
      displayName: 'Direction',
      type: 'select',
      default: 'Long',
      options: [
        { value: 'Long', label: 'Long' },
        { value: 'Short', label: 'Short' },
      ],
    },

    {
      name: 'offset',
      displayName: 'Price Offset',
      type: 'float',
      default: 0,
      dependsOn: {
        parameter: 'entryType',
        values: ['Limit'],
      },
    },
    {
      name: 'expirationType',
      displayName: 'Expiration',
      type: 'select',
      default: 'GTC',
      options: [
        { value: 'GTC', label: 'Good Till Cancelled' },
        { value: 'Duration', label: 'Duration' },
        { value: 'EndOfDay', label: 'Cancel at End of Day' },
        { value: 'Close', label: 'Cancel on Close' },
        { value: 'SpecificTime', label: 'Cancel at Specific Time' },
      ],
      dependsOn: { parameter: 'entryType', values: ['Limit'] },
    },
    {
      name: 'expirationDuration',
      displayName: 'Duration',
      type: 'int',
      default: 10,
      min: 1,
      dependsOn: { parameter: 'entryType', values: ['Limit'] },
      dependsOnAll: [{ parameter: 'expirationType', values: ['Duration'] }],
    },
    {
      name: 'expirationUnit',
      displayName: 'Duration Unit',
      type: 'select',
      default: 'Minutes',
      options: [
        { value: 'Seconds', label: 'Seconds' },
        { value: 'Minutes', label: 'Minutes' },
        { value: 'Bars', label: 'Bars' },
      ],
      dependsOn: { parameter: 'entryType', values: ['Limit'] },
      dependsOnAll: [{ parameter: 'expirationType', values: ['Duration'] }],
    },
    {
      name: 'expirationTime',
      displayName: 'Expiration Time (ET)',
      type: 'time',
      native: true,
      default: '11:30',
      dependsOn: { parameter: 'entryType', values: ['Limit'] },
      dependsOnAll: [{ parameter: 'expirationType', values: ['SpecificTime'] }],
    },
    ...entryWindowParameters,
  ],
}

// Position Sizing Node
const positionSizingSchema: UniversalNodeSchema = {
  type: 'positionSizing',
  // The Trade Manager replaces it; still loads, no longer added (2026-10-06).
  addable: false,
  // The Trade Manager's sizing setting; the AI builds with that (2026-10-06 swap).
  compound: { catalog: { ai: false } },
  displayName: 'Position Sizing / Risk',
  category: 'entry-exit-logic',
  icon: PieChart,
  inputHandler: {
    type: 'single',
  },
  inputs: [
    {
      id: 'signal',
      displayName: 'Dynamic Input',
      acceptedTypes: ['number'],
      acceptedSources: ['*'],
      // Only Risk ($) divides by the signal (risk budget ÷ stop distance,
      // per the cookbook risk-sizing recipe); Shares / Value ($) /
      // % Portfolio are fixed-quantity methods that ignore it.
      activeWhen: { parameter: 'method', is: ['Risk ($)'] },
    },
  ],
  outputs: [
    {
      id: 'sizing',
      displayName: 'Sizing',
      type: 'number',
    },
  ],
  parameters: [
    {
      name: 'method',
      displayName: 'Sizing Method',
      type: 'select',
      default: 'Shares',
      options: [
        { value: 'Shares', label: 'Shares' },
        { value: 'Risk ($)', label: 'Risk ($)' },
        { value: 'Value ($)', label: 'Value ($)' },
        { value: '% Portfolio', label: '% Portfolio' },
      ],
    },
    {
      name: 'value',
      displayName: 'Value',
      type: 'float',
      default: 100,
    },
  ],
}

// Target Node
const targetSchema: UniversalNodeSchema = {
  type: 'target',
  // A mode that stops reading the signal lets go of its wire
  // (utils/graph-commands planParamChange).
  face: { releaseInactive: true, inputControl: 'source' },
  displayName: 'Target',
  category: 'entry-exit-logic',
  icon: Target,
  inputHandler: {
    type: 'single',
  },
  inputs: [
    {
      id: 'signal',
      displayName: '',
      acceptedTypes: ['number', 'boolean'],
      acceptedSources: ['*'],
      // Ticks / %-Trade targets compute off the entry price; the signal is
      // load-bearing only when it IS the level (Static Value) or the exit
      // trigger (Condition).
      activeWhen: { parameter: 'targetType', is: ['Static Value', 'Condition'] },
      fieldSelection: {
        dynamic: true,
        defaultField: 'close',
        fallbackOptions: [
          { value: 'open', label: 'Open' },
          { value: 'high', label: 'High' },
          { value: 'low', label: 'Low' },
          { value: 'close', label: 'Close' },
          { value: 'volume', label: 'Volume' },
        ],
      },
    },
  ],
  outputs: [
    {
      id: 'target',
      displayName: 'Target',
      type: 'number',
    },
  ],
  parameters: [
    {
      name: 'targetType',
      displayName: 'Target Type',
      type: 'select',
      default: '% Trade',
      options: [
        { value: 'Ticks', label: 'Ticks' },
        { value: '% Trade', label: 'PnL %' },
        { value: 'Static Value', label: 'Static Value' },
        { value: 'Condition', label: 'Condition' },
      ],
    },
    {
      name: 'percentageValue',
      displayName: 'Value',
      type: 'float',
      default: 10,
      min: 0.1,
      max: 1000,
      dependsOn: {
        parameter: 'targetType',
        values: ['Ticks', '% Trade'],
      },
    },
    {
      name: 'priceOffset',
      displayName: 'Price Offset',
      type: 'float',
      default: 0,
      dependsOn: {
        parameter: 'targetType',
        values: ['Static Value'],
      },
    },
  ],
}

// Stop Node
const stopSchema: UniversalNodeSchema = {
  type: 'stop',
  // The Trade Manager replaces it; still loads, no longer added (2026-10-06).
  addable: false,
  // Part of the cluster the Trade Manager lowers to; the AI sets the Trade Manager's stop
  // instead (2026-10-06 vocabulary swap).
  compound: { catalog: { ai: false } },
  // A mode that stops reading the signal lets go of its wire
  // (utils/graph-commands planParamChange).
  face: { releaseInactive: true },
  displayName: 'Stop',
  category: 'entry-exit-logic',
  icon: Shield,
  inputHandler: {
    type: 'single',
  },
  inputs: [
    {
      id: 'signal',
      displayName: '',
      acceptedTypes: ['number'],
      acceptedSources: ['*'],
      // Ticks / %-Trade stops compute off the entry price; only Static Value
      // reads the signal (it carries the invalidation distance — see the
      // cookbook ATR-stop recipe).
      activeWhen: { parameter: 'stopType', is: ['Static Value'] },
      fieldSelection: {
        dynamic: true,
        defaultField: 'close',
      },
    },
  ],
  outputs: [
    {
      id: 'stop',
      displayName: 'Stop',
      type: 'number',
    },
  ],
  parameters: [
    {
      name: 'stopType',
      displayName: 'Exit Type',
      type: 'select',
      default: '% Trade',
      options: [
        { value: 'Ticks', label: 'Ticks' },
        { value: '% Trade', label: 'PnL %' },
        { value: 'Static Value', label: 'Static Value' },
      ],
    },
    {
      name: 'percentageValue',
      displayName: 'Value',
      type: 'float',
      default: 5,
      min: 0.1,
      max: 1000,
      dependsOn: {
        parameter: 'stopType',
        values: ['Ticks', '% Trade'],
      },
    },
    {
      name: 'priceOffset',
      displayName: 'Price Offset',
      type: 'float',
      default: 0,
      dependsOn: {
        parameter: 'stopType',
        values: ['Static Value'],
      },
    },
  ],
}

// Multi-Target Node
const multiTargetSchema: UniversalNodeSchema = {
  type: 'multiTarget',
  displayName: 'Multi Target',
  category: 'entry-exit-logic',
  icon: GitBranch,
  face: { inputControl: 'source' },
  inputHandler: {
    type: 'multi',
    maxInputs: 4,
    reveal: 'progressive',
  },
  inputs: [
    {
      id: 'target1',
      displayName: 'Target 1',
      acceptedTypes: ['number'],
      acceptedSources: ['target'],
      nodeTypeFilter: ['target'],
      emptyText: 'Connect a Target node',
    },
    {
      id: 'target2',
      displayName: 'Target 2',
      acceptedTypes: ['number'],
      acceptedSources: ['target'],
      nodeTypeFilter: ['target'],
      emptyText: 'Connect a Target node',
    },
    {
      id: 'target3',
      displayName: 'Target 3',
      acceptedTypes: ['number'],
      acceptedSources: ['target'],
      nodeTypeFilter: ['target'],
      emptyText: 'Connect a Target node',
    },
    {
      id: 'target4',
      displayName: 'Target 4',
      acceptedTypes: ['number'],
      acceptedSources: ['target'],
      nodeTypeFilter: ['target'],
      emptyText: 'Connect a Target node',
    },
  ],
  outputs: [
    {
      id: 'target',
      displayName: 'Combined Target',
      type: 'number',
    },
  ],
  parameters: [
    {
      name: 'target1Percent',
      displayName: 'Target 1 %',
      type: 'float',
      default: 34,
      min: 0,
      max: 100,
    },
    {
      name: 'target2Percent',
      displayName: 'Target 2 %',
      type: 'float',
      default: 33,
      min: 0,
      max: 100,
    },
    {
      name: 'target3Percent',
      displayName: 'Target 3 %',
      type: 'float',
      default: 33,
      min: 0,
      max: 100,
    },
    {
      name: 'target4Percent',
      displayName: 'Target 4 %',
      type: 'text',
      default: '0',
    },
  ],
}

// Trade Node (Advanced)
const tradeSchema: UniversalNodeSchema = {
  type: 'trade',
  // The Trade Manager replaces it; still loads, no longer added (2026-10-06).
  addable: false,
  // The Trade Manager stands for it; the AI builds with that (2026-10-06 swap).
  compound: { placesTrades: true, catalog: { ai: false } },
  displayName: 'Advanced Trade',
  category: 'entry-exit-logic',
  icon: TrendingUp,
  face: { inputControl: 'none' },
  inputHandler: {
    type: 'multi',
    minInputs: 5,
    maxInputs: 6,
  },
  inputs: [
    {
      id: 'trigger',
      displayName: 'Trigger',
      acceptedTypes: ['boolean'],
      acceptedSources: ['*'],
    },
    {
      id: 'entry',
      displayName: 'Entry',
      acceptedTypes: ['boolean'],
      acceptedSources: ['entry-exit-logic'],
      nodeTypeFilter: ['entryRules'],
    },
    {
      id: 'target',
      displayName: 'Target',
      acceptedTypes: ['number'],
      acceptedSources: ['entry-exit-logic'],
      nodeTypeFilter: ['target', 'multiTarget'],
    },
    {
      id: 'exit',
      displayName: 'Exit',
      acceptedTypes: ['number'],
      acceptedSources: ['entry-exit-logic'],
      nodeTypeFilter: ['stop'],
    },
    {
      id: 'sizing',
      displayName: 'Size',
      acceptedTypes: ['number'],
      acceptedSources: ['entry-exit-logic'],
      nodeTypeFilter: ['positionSizing'],
    },
    {
      id: 'trailing',
      displayName: 'Trailing',
      acceptedTypes: ['number'],
      acceptedSources: ['entry-exit-logic'],
      nodeTypeFilter: ['trailingStop'],
      // Only drawn and wired once the Trailing Stop switch is on.
      activeWhen: { parameter: 'enableTrailing', is: [true] },
    },
  ],
  outputs: [],
  parameters: [
    {
      name: 'enableTrailing',
      displayName: 'Trailing Stop',
      type: 'boolean',
      default: false,
    },
    {
      // Optional entry-count limiter. 0 = unlimited (default; no behavior change).
      // When > 0, the strategy stops taking NEW entries after this many; exits
      // are unaffected. Read by the codegen off this 'trade' node.
      name: 'maxEntries',
      displayName: 'Max Entries',
      type: 'int',
      default: 0,
      min: 0,
    },
  ],
  ui: runSection,
}

// Simple Trade Node
const simpleTradeSchema: UniversalNodeSchema = {
  type: 'simpleTrade',
  // The Trade Manager replaces it; still loads, no longer added (2026-10-06).
  addable: false,
  // The Trade Manager stands for it; the AI builds with that (2026-10-06 swap).
  compound: { placesTrades: true, catalog: { ai: false } },
  facets: { entryWindow: { trigger: 'entry' } },
  displayName: 'Simple Trade',
  category: 'entry-exit-logic',
  icon: ArrowRight,
  face: { inputControl: 'none' },
  inputHandler: {
    type: 'single',
  },
  inputs: [
    {
      id: 'entry',
      displayName: '',
      acceptedTypes: ['boolean'],
      acceptedSources: ['logic', 'indicators', 'filters'],
    },
    entryWindowInput,
  ],
  outputs: [],
  parameters: [
    {
      name: 'orderDirection',
      displayName: 'Direction',
      type: 'select',
      default: 'Long',
      options: [
        { value: 'Long', label: 'Long' },
        { value: 'Short', label: 'Short' },
      ],
    },
    {
      name: 'takeProfitPercent',
      displayName: 'Take Profit %',
      type: 'float',
      default: 2.0,
      min: 0.1,
      max: 100.0,
      step: 0.1,
    },
    {
      name: 'stopLossPercent',
      displayName: 'Stop Loss %',
      type: 'float',
      default: 1.0,
      min: 0.1,
      max: 100.0,
      step: 0.1,
    },
    {
      // Optional entry-count limiter. 0 = unlimited (default; no behavior change).
      // When > 0, the strategy stops taking NEW entries after this many; exits
      // are unaffected.
      name: 'maxEntries',
      displayName: 'Max Entries',
      type: 'int',
      default: 0,
      min: 0,
    },
    ...entryWindowParameters,
  ],
  ui: runSection,
}

// Trailing Stop Node
const trailingStopSchema: UniversalNodeSchema = {
  type: 'trailingStop',
  // The Trade Manager replaces it; still loads, no longer added (2026-10-06).
  addable: false,
  // Part of the cluster the Trade Manager lowers to; the AI sets the Trade Manager's trailing settings
  // instead (2026-10-06 vocabulary swap).
  compound: { catalog: { ai: false } },
  displayName: 'Trailing Stop',
  category: 'entry-exit-logic',
  icon: TrendingDown,
  inputHandler: {
    type: 'single',
  },
  inputs: [
    {
      id: 'signal',
      displayName: '',
      acceptedTypes: ['boolean', 'number'],
      acceptedSources: ['*'],
      // % Trade and Ticks trail by trailDistance; only Condition reads a signal.
      activeWhen: { parameter: 'trailType', is: ['Condition'] },
      fieldSelection: {
        dynamic: true,
        defaultField: 'close',
      },
    },
  ],
  outputs: [
    {
      id: 'stop',
      displayName: 'Trailing Stop',
      type: 'number',
    },
  ],
  parameters: [
    {
      name: 'trailType',
      displayName: 'Trail Type',
      type: 'select',
      default: '% Trade',
      options: [
        { value: '% Trade', label: '% Trade' },
        { value: 'Ticks', label: 'Ticks' },
        { value: 'Condition', label: 'Condition' },
      ],
    },
    {
      name: 'trailDistance',
      displayName: 'Trail Distance',
      type: 'float',
      default: 2.0,
      min: 0.1,
      max: 100,
      step: 0.1,
      dependsOn: {
        parameter: 'trailType',
        values: ['% Trade', 'Ticks'],
      },
    },
    {
      name: 'activationTrigger',
      displayName: 'Activation',
      type: 'select',
      default: 'Immediate',
      options: [
        { value: 'Immediate', label: 'Immediate' },
        { value: 'Breakeven', label: 'Breakeven' },
        { value: 'Profit %', label: '% Profit' },
      ],
    },
    {
      name: 'activationValue',
      displayName: 'Profit % Value',
      type: 'float',
      default: 1.0,
      min: 0.1,
      max: 100,
      step: 0.1,
      dependsOn: {
        parameter: 'activationTrigger',
        values: ['Profit %'],
      },
    },
  ],
}

/**
 * Registration function to add all strategy node schemas to the universal registry
 */
export function registerStrategySchemas(): void {
  // Register all strategy node schemas
  UniversalNodeRegistry.register(entryRulesSchema)
  UniversalNodeRegistry.register(positionSizingSchema)
  UniversalNodeRegistry.register(targetSchema)
  UniversalNodeRegistry.register(stopSchema)
  UniversalNodeRegistry.register(trailingStopSchema)
  UniversalNodeRegistry.register(multiTargetSchema)
  UniversalNodeRegistry.register(tradeSchema)
  UniversalNodeRegistry.register(simpleTradeSchema)

  console.log(
    'Registered 8 Strategy Node Schemas:',
    'Entry Rules, Position Sizing, Target, Stop, Trailing Stop, Multi Target, Advanced Trade, Simple Trade',
  )
}

// Export individual schemas if needed
export {
  entryRulesSchema,
  positionSizingSchema,
  targetSchema,
  stopSchema,
  trailingStopSchema,
  multiTargetSchema,
  tradeSchema,
  simpleTradeSchema,
}
